import { useEffect, useState } from 'react';
import { usePersonProfileStore } from '../../store/personProfileStore';
import { findOrgPerson } from '../../lib/org-person-lookup';
import { PersonDetailPopup, type OrgPerson } from '../PersonDetailPopup';

/**
 * 사람 프로필의 **전역 자리** (2026-08-31 · 시안 맞춤 8/9).
 *
 * ★★ `SignatureHost` 와 같은 이유로 셸이 든다 — 여는 쪽(메시지 줄 · 목록 행)은 스크롤되며
 *    사라질 수 있고, 그 안에서 그리면 팝업까지 함께 사라진다.
 *
 * ★★ **팝업은 한 벌이다.** 조직도·채팅 참여자가 쓰던 `PersonDetailPopup` 을 그대로 연다 —
 *    두 벌로 만들면 같은 사람이 두 모습으로 갈라진다(그 컴포넌트가 생긴 이유가 그것이다).
 *
 * ★ 조직 색인은 **한 번만** 만들어 모듈에 캐시된다(`org-person-lookup`). 색인에 없으면
 *   부르는 쪽이 준 `hint` 로 최소한을 채워 연다 — 팝업 안에서 서버가 범위대로 다시 채운다.
 */
export function PersonProfileHost(): JSX.Element | null {
  const userId = usePersonProfileStore((s) => s.userId);
  const hint = usePersonProfileStore((s) => s.hint);
  const close = usePersonProfileStore((s) => s.close);
  const [person, setPerson] = useState<OrgPerson | null>(null);

  useEffect(() => {
    if (!userId) { setPerson(null); return; }
    let alive = true;
    // 아는 만큼으로 **먼저 연다** — 색인을 기다리며 아무것도 안 뜨면 눌러도 안 되는 것으로 읽힌다.
    setPerson(fallbackPerson(userId, hint));
    void findOrgPerson(userId)
      .then((found) => { if (alive && found) setPerson(found); })
      .catch(() => { /* 색인 실패는 조용히 — 아는 만큼으로 이미 떠 있다 */ });
    return () => { alive = false; };
  }, [userId, hint]);

  if (!userId || !person) return null;
  return <PersonDetailPopup person={person} onClose={close} onNavigated={close} />;
}

/** 조직 색인에 없을 때의 최소 한 명 — 팝업이 요구하는 칸만 채운다. */
function fallbackPerson(userId: string, hint: { name?: string | null; avatarUrl?: string | null; position?: string | null; email?: string | null } | null): OrgPerson {
  return {
    id: userId,
    email: hint?.email ?? '',
    name: hint?.name ?? '',
    position: hint?.position ?? null,
    role: 'STAFF',
    status: 'offline',
    isActive: true,
    avatarUrl: hint?.avatarUrl ?? null,
    divisionName: null,
    teamName: null,
    duty: null,
  } as OrgPerson;
}
